import { useCallback, useContext, useEffect, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import { Box, Stack } from "@mui/system";
import theme from "../../../muiTheme";
import ManagementsUpdateHeader from "../ManagementsUpdateHeader/ManagementsUpdateHeader";
import { Grid } from "@mui/material";
import "./ManagementsUpdatePowers.scss";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";
import {
  FormattedOwnerData,
  OwnerDataCups,
} from "../../../core/models/ownerData/ownerData.model";
import ManagementsUpdatePowersCupsSelection from "./ManagementsUpdatePowersCupsSelection/ManagementsUpdatePowersCupsSelection";
import ManagementsUpdateNewPowers from "./ManagementsUpdateNewPowers/ManagementsUpdateNewPowers";
import {
  GetSuppliesDetailRequestBody,
  SuppliesDetailObject,
} from "../../../core/models/suppliesDetail/suppliesDetail.model";
import {
  formatCupsDetail,
  initialSuppliesDetail,
} from "../../../utils/components/suppliesDetail/suppliesDetail.util";
import { getSuppliesDetail as getSuppliesDetailFetch } from "../../../core/services/suppliesDetail/suppliesDetail.service";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  fetchBipValidation,
  postCallCenter,
  postImportDocument,
} from "../../../core/services/managements/managements.service";
import {
  getBipValidationRequestBody,
  ManagementRequestInfo,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
  UpdatePowersReferenceData,
} from "../../../core/models/managements/managements.model";
import {
  MANAGEMENTS_REGISTER_TYPE_REQUEST,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
} from "../../../core/constants/constants";
import {
  getManagementsRequestTypeData,
  getRegisterTypeId,
  initialManagementRequestInfo,
  initialRequestStateData,
} from "../../../utils/components/managements/managements.util";
import { formatDate } from "../../../utils/dateUtils/formatDate.util";
import { getRecipientId } from "../../../utils/components/services/services.util";
import useOcrValidation from "../../../utils/components/managements/ocrValidation/useOcrValidation";
import { NewPowers } from "../../../core/models/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers";
import { isEqual } from "lodash";

const ManagementsUpdatePowers = () => {
  const { ownerData, userData, cifCupsState, isAgent } =
    useContext(TotalEnergiesContext);
  const [step, setStep] = useState<number>(-1);
  const [ownerDataCupSelected, setOwnerDataCupSelected] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  const [cupsDetail, setCupsDetail] = useState<SuppliesDetailObject>(
    initialSuppliesDetail,
  );

  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>(initialRequestStateData);
  const [isCupsLoading, setIsCupsLoading] = useState<boolean>(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [requiredFile, setRequiredFile] = useState<File | null>(null);
  const [managementRequestInfo, setManagementRequestInfo] =
    useState<ManagementRequestInfo>(initialManagementRequestInfo);
  const [contractCode, setContractCode] = useState<string>("");
  const [processedOwnerData, setProcessedOwnerData] =
    useState<FormattedOwnerData | null>(null);
  const [cupsError, setCupsError] = useState<
    "renovation_error" | "expired" | "atr_checked" | null
  >(null);
  const [newPowers, setNewPowers] = useState<NewPowers>({});
  const [maximeter, setMaximeter] = useState<boolean>(true);
  const [appliedDayFormatted, setAppliedDayFormatted] = useState<string | null>(
    null,
  );

  const {
    getBase64file,
    ocrError,
    setOcrError,
    isOcrLoading,
    setIsOcrLoading,
    setOcrErrorMessageKeys,
    formatOcrErrorMessageText,
  } = useOcrValidation();

  useEffect(() => {
    if (!!cupsError) {
      setCupsError(null);
    }
    const contractSituation = isAgent
      ? ownerDataCupSelected.situation
      : ownerDataCupSelected.contractSituationDetail;

    const atrCheckSelectedCup = cifCupsState?.listCupsCifRaw
      ?.flatMap((cifCup) => cifCup.cups)
      ?.find((c) => c.cupsCode === ownerDataCupSelected.codeCups)?.atrCheck;

    if (atrCheckSelectedCup) {
      setCupsError("atr_checked");
      return;
    }

    if (!isEqual(ownerDataCupSelected, initialOwnerDataCup)) {
      if (
        contractSituation === "Pendiente Renovación" ||
        contractSituation === "Renovación aceptada" ||
        contractSituation === "Trámite Renovación" ||
        contractSituation === "Renovación Pendiente Comercial"
      ) {
        setCupsError("renovation_error");
      } else if (
        contractSituation !== "Cortado" &&
        contractSituation !== "Contrato"
      ) {
        setCupsError("expired");
      }
    }

    return () => {
      setCupsError(null);
    };
  }, [ownerDataCupSelected]);

  useEffect(() => {
    //add situation property to ownerData for agents
    if (isAgent && ownerData && cifCupsState) {
      const updatedOwnerData = {
        ...ownerData,
        cups: ownerData.cups.map((cup) => {
          const foundCifCup = cifCupsState.listCupsCifRaw.find((cifCup) =>
            cifCup.cups.some((c) => c.cupsCode === cup.codeCups),
          );

          if (foundCifCup) {
            const foundCup = foundCifCup.cups.find(
              (c) => c.cupsCode === cup.codeCups,
            );
            if (foundCup?.situation) {
              return {
                ...cup,
                situation: foundCup.situation,
              };
            }
          }

          return cup;
        }),
      };

      setProcessedOwnerData(updatedOwnerData);
    } else {
      setProcessedOwnerData(ownerData || null);
    }
  }, [isAgent, ownerData, cifCupsState]);

  const getSuppliesDetail = useCallback(
    async (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      token: string,
      { idCups, idContract }: OwnerDataCups,
      abortSignal: AbortSignal,
    ) => {
      const getSuppliesRequestBody: GetSuppliesDetailRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        isAdmin: isAdministrator.toString(),
        cupsContractId: idContract,
        cupsId: idCups,
      };

      await getSuppliesDetailFetch(getSuppliesRequestBody, token, abortSignal)
        .then((response) => {
          setCupsDetail(formatCupsDetail(response));
          setContractCode(response.CodigoContrato);
          setIsCupsLoading(false);
        })
        .catch((error) => {
          setIsCupsLoading(false);
        });
    },
    [setIsCupsLoading],
  );

  useEffect(() => {
    if (!!ocrError) {
      setOcrError(null);
      setOcrErrorMessageKeys([]);
    }
  }, [
    requiredFile,
    maximeter,
    ownerDataCupSelected,
    newPowers,
    appliedDayFormatted,
  ]);

  const validateDocumentOcr = async (): Promise<string> => {
    setIsOcrLoading(true);

    try {
      const base64file = await getBase64file(requiredFile);

      const referenceDataPowersKeys = Array(6)
        .fill("")
        .map((_, i) => {
          return `potencia_actual_p${i + 1}`;
        });

      const referenceDataNewPowersKeys = Array(6)
        .fill("")
        .map((_, i) => {
          return `potencia_solicitada_p${i + 1}`;
        });

      const referenceDataPowers = referenceDataPowersKeys.map((item, i) => {
        const powers =
          cupsDetail.powers
            .find((power) => power.period === i + 1)
            ?.hiredPower.toString() ?? "";

        return { [item]: powers };
      });

      const referenceDataNewPowers = referenceDataNewPowersKeys.map(
        (item, i) => {
          const powers = newPowers[`p${i + 1}`];

          return { [item]: powers ?? "" };
        },
      );

      const referenceData: UpdatePowersReferenceData = {
        razon_social: cupsDetail.socialReason,
        cif: ownerDataCupSelected.cif,
        direccion_suministro: cupsDetail.address,
        numero_suministro: cupsDetail.addressNumber,
        localidad_suministro: cupsDetail.town,
        provincia_suministro: cupsDetail.province,
        cp_suministro: cupsDetail.zipCode,
        cups: ownerDataCupSelected.codeCups.replace("ES", ""),
        tarifa_actual: cupsDetail.rate.replace(" ", "").replace("ML", ""),
        ...Object.assign({}, ...referenceDataPowers),
        ...Object.assign({}, ...referenceDataNewPowers),
        tarifa_solicitada: cupsDetail.rate.replace(" ", "").replace("ML", ""),
        maximetro: maximeter,
        fecha_solicitud: appliedDayFormatted,
      };

      const ocrValidationRequestBody: getBipValidationRequestBody = {
        document_type: "cambio_potencia",
        pdf_base64: base64file ?? "",
        reference_data: referenceData,
      };

      const ocrValidationResponse = await fetchBipValidation(
        ocrValidationRequestBody,
      );

      if (
        ocrValidationResponse?.validation_result?.message === "Validation ko"
      ) {
        setOcrError("response_ko");
        const ocrMessages = formatOcrErrorMessageText(ocrValidationResponse);
        return `; Los siguientes campos del documento adjunto presentan errores de validación: ${ocrMessages}`;
      }

      if (
        ocrValidationResponse?.validation_result?.message === "Validation ok"
      ) {
        return "; El documento adjunto ha sido validado correctamente";
      }
    } catch (error: any) {
      if (error?.status === 400) {
        setOcrError("response_ko");
        const errorDetail =
          typeof error?.detail === "string" ? error.detail : "";
        if (errorDetail.includes("INVALID_DOCUMENT_TYPE")) {
          return "; El documento adjunto no es válido";
        }
        setOcrError("server_error");
        return "; El servicio de OCR ha fallado.";
      } else {
        setOcrError("server_error");
        return "; El servicio de OCR ha fallado.";
      }
    } finally {
      setIsOcrLoading(false);
    }
    return "";
  };

  const handleSubmit = async (
    incidenceText: string,
    ownerDataCupSelected: OwnerDataCups,
  ): Promise<void> => {
    const { envirorment, idContract } = ownerDataCupSelected;
    let ocrMessages: string = "";

    if (isAgent) {
      ocrMessages = await validateDocumentOcr();
    }

    const managementMessage = `El usuario ha realizado una petición de cambio de potencia en el suministro de luz. ${
      incidenceText ? incidenceText : ""
    }${ocrMessages}`;

    if (userData) {
      const IdTipoRegistro = getRegisterTypeId(
        envirorment,
        MANAGEMENTS_REGISTER_TYPE_REQUEST,
      );
      const requestBody: PostCallCenterRequestBody = {
        TextoIncidencia: managementMessage,
        CodigosContratos: [+contractCode],
        IdsContratos: [+idContract],
        IdTipoRegistro,
        IdSubTipoReclamacion: MANAGEMENTS_SUBTYPE_UPDATE_POWERS,
        idUsuarioDestino: getRecipientId(
          IdTipoRegistro,
          MANAGEMENTS_SUBTYPE_UPDATE_POWERS,
        ),
        ClaveMedio: isAgent ? "09" : "12",
      };

      setIsUpdateLoading(true);
      setStep(1);

      postCallCenter(requestBody, userData.token)
        .then(([{ IdCallCenter, IdCarpetaIncidencias }]) => ({
          IdCallCenter,
          IdCarpetaIncidencias,
        }))
        .then(async ({ IdCallCenter, IdCarpetaIncidencias }) => {
          if (IdCallCenter === 0) {
            throw new Error("IdCallCenter is 0");
          }

          file &&
            (await postImportDocument(
              IdCallCenter,
              IdCarpetaIncidencias,
              file,
              userData.token,
            ));

          requiredFile &&
            (await postImportDocument(
              IdCallCenter,
              IdCarpetaIncidencias,
              requiredFile,
              userData.token,
            ));

          setRequestState(MANAGEMENTS_STATE_PENDING);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
              MANAGEMENTS_STATE_PENDING,
              isAgent ?? false,
            ),
          );
          setManagementRequestInfo({
            registrarionDate: formatDate(new Date()),
            refferenceNumber: IdCallCenter.toString(),
          });

          setIsUpdateLoading(false);
        })
        .catch((error) => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
          setIsUpdateLoading(false);
        });
    }
  };

  useEffect(() => {
    const suppliesDetailsAbortController = new AbortController();

    if (processedOwnerData && userData && step === 0) {
      getSuppliesDetail(
        processedOwnerData,
        userData.token,
        ownerDataCupSelected,
        suppliesDetailsAbortController.signal,
      );

      setIsCupsLoading(true);
    }
  }, [
    getSuppliesDetail,
    processedOwnerData,
    ownerDataCupSelected,
    step,
    userData,
  ]);

  return (
    <Box component="section" className="management-update-powers">
      <ManagementsUpdateHeader
        title={"Modificacion de potencias"}
        step={step}
        cupsDetail={cupsDetail}
        isLoading={isCupsLoading}
        showDetailCups={true}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={"2.5rem"}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        {step === -1 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="div"
                className="management-update-powers__content-container"
              >
                <ManagementsUpdatePowersCupsSelection
                  cupsError={cupsError}
                  ownerDataCups={processedOwnerData?.cups || []}
                  ownerDataCupSelected={ownerDataCupSelected}
                  setOwnerDataCupSelected={setOwnerDataCupSelected}
                  setStep={setStep}
                />
              </Stack>
            </Grid>
          </Grid>
        )}
        {step === 0 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="div"
                className="management-update-powers__content-container-alt"
                boxShadow={{
                  xs: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)",
                  md: "",
                }}
              >
                <ManagementsUpdateNewPowers
                  setAppliedDayFormatted={setAppliedDayFormatted}
                  maximeter={maximeter}
                  setMaximeter={setMaximeter}
                  isOcrLoading={isOcrLoading}
                  isAgent={isAgent ?? false}
                  cupsDetail={cupsDetail}
                  isLoading={isCupsLoading}
                  setFile={setFile}
                  setRequiredFile={setRequiredFile}
                  handleSubmit={(incidenceText) =>
                    handleSubmit(incidenceText, ownerDataCupSelected)
                  }
                  requiredFile={requiredFile}
                  setNewPowers={setNewPowers}
                  newPowers={newPowers}
                />
              </Stack>
            </Grid>
          </Grid>
        )}
        {step === 1 && (
          <ManagementsRequestState
            showManagementRequestInfo
            isLoading={isUpdateLoading}
            state={requestState}
            managementRequestInfo={managementRequestInfo}
            title={requestStateData.title}
            description={requestStateData.description}
            descriptionExtra={requestStateData.descriptionExtra}
          />
        )}
      </Stack>
    </Box>
  );
};

export default ManagementsUpdatePowers;
